import * as React from "react";
import { select, scaleLinear, axisBottom, axisLeft, extent, schemeTableau10, schemeSet3 } from "d3";

const COLORS = [...schemeTableau10, ...schemeSet3];
const WIDTH = 760;
const HEIGHT = 520;
const MARGIN = { top: 16, right: 20, bottom: 52, left: 62 };
const CHI2_95_2DOF = 5.991; // 95% confidence ellipse for a bivariate normal

const colorFor = (index) => COLORS[index % COLORS.length];
const baseName = (filename) => filename.replace(/\.csv$/i, "");
const percent = (value, digits = 1) => `${(value * 100).toFixed(digits)}%`;

// Points of the 95% confidence ellipse of a set of [x, y] points (null if there are too few points / no spread).
function confidenceEllipse(points) {
    const n = points.length;
    if (n < 3) return null;
    const mx = points.reduce((s, p) => s + p[0], 0) / n;
    const my = points.reduce((s, p) => s + p[1], 0) / n;
    let sxx = 0, syy = 0, sxy = 0;
    points.forEach(([x, y]) => {
        sxx += (x - mx) ** 2;
        syy += (y - my) ** 2;
        sxy += (x - mx) * (y - my);
    });
    sxx /= n - 1; syy /= n - 1; sxy /= n - 1;
    const trace = sxx + syy;
    const det = sxx * syy - sxy * sxy;
    const gap = Math.sqrt(Math.max(trace * trace / 4 - det, 0));
    const l1 = trace / 2 + gap;
    const l2 = trace / 2 - gap;
    if (l1 <= 0 || l2 <= 0) return null;
    const angle = 0.5 * Math.atan2(2 * sxy, sxx - syy);
    const a = Math.sqrt(CHI2_95_2DOF * l1);
    const b = Math.sqrt(CHI2_95_2DOF * l2);
    const steps = 60;
    return Array.from({ length: steps + 1 }, (_, i) => {
        const t = (i / steps) * 2 * Math.PI;
        const ex = a * Math.cos(t);
        const ey = b * Math.sin(t);
        return [mx + ex * Math.cos(angle) - ey * Math.sin(angle), my + ex * Math.sin(angle) + ey * Math.cos(angle)];
    });
}

// Same rule as backend/scripts/qaqc_pca.py: a "samples in rows" CSV with a column named Batch / Batch ID / ... is pre-batched.
const BATCH_COLUMN = /^\s*batch\b|^\s*batch[_ -]?(id|no|num|number|name)?\s*$/i;

// Splits the first `limit` fields of one CSV line (handles quoted fields containing commas).
function csvFields(line, limit = Infinity) {
    const fields = [];
    let field = "", quoted = false;
    for (let i = 0; i < line.length && fields.length < limit; i++) {
        const ch = line[i];
        if (quoted) {
            if (ch === '"' && line[i + 1] === '"') { field += '"'; i++; }
            else if (ch === '"') quoted = false;
            else field += ch;
        } else if (ch === '"') quoted = true;
        else if (ch === ",") { fields.push(field); field = ""; }
        else field += ch;
    }
    if (fields.length < limit) fields.push(field);
    return fields;
}

// Returns { column, batches } if the file has a batch column, otherwise null (also null if the file can't be read).
async function sniffPrebatched(file) {
    try {
        const lines = (await file.text()).replace(/^\uFEFF/, "").split(/\r?\n/).filter((l) => l.trim());
        if (lines.length < 2) return null;
        const header = csvFields(lines[0]);
        const col = header.findIndex((h, i) => i > 0 && BATCH_COLUMN.test(h));
        if (col < 0) return null;
        const values = new Set(lines.slice(1).map((l) => (csvFields(l, col + 1)[col] || "").trim() || "Unassigned"));
        return { column: header[col].trim(), batches: values.size };
    } catch (e) {
        return null;
    }
}

const csvCell = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

// Samples in rows, metabolites in columns, with a Batch column (the same layout the page accepts as a pre-batched upload).
function downloadCorrectedCsv(result) {
    const { features, rows } = result.corrected_data;
    const lines = [["Sample", "Batch", ...features].map(csvCell).join(",")];
    rows.forEach((values, i) => {
        lines.push([result.points[i].sample, result.points[i].batch, ...values].map(csvCell).join(","));
    });
    const url = URL.createObjectURL(new Blob([lines.join("\r\n") + "\r\n"], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "combat_corrected.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

// A metabolite's batch effect is flagged when it is significant (FDR q < 0.05) and batch explains enough of its variance.
const SIGNIFICANT_Q = 0.05;
const STRONG_R2 = 0.5;
const MODERATE_R2 = 0.2;
const HIGH_MISSING = 0.2; // above this, imputed values can create or hide a batch effect

function batchEffectLevel(m) {
    if (m.q === null || m.q >= SIGNIFICANT_Q) return null;
    if (m.r2 >= STRONG_R2) return "strong";
    if (m.r2 >= MODERATE_R2) return "moderate";
    return null;
}

const formatQ = (q) => (q === null ? "–" : q < 0.001 ? q.toExponential(1) : q.toFixed(3));
const formatFold = (fold) => (fold === null ? "–" : fold >= 1 ? `${fold.toFixed(2)}× higher` : `${(1 / fold).toFixed(2)}× lower`);

function downloadMetabolitesCsv(metabolites) {
    const hasAfter = metabolites.some((m) => m.r2_after !== undefined);
    const header = ["Metabolite", "Batch effect", "Batch R2", "p", "q (FDR)", "Most different batch", "Fold change vs other batches", "Missing fraction"];
    if (hasAfter) header.push("Batch R2 after ComBat");
    const lines = [header.map(csvCell).join(",")];
    metabolites.forEach((m) => {
        const row = [m.name, batchEffectLevel(m) || "", m.r2.toFixed(4), m.p, m.q, m.most_different_batch, m.fold_change, m.missing_fraction.toFixed(3)];
        if (hasAfter) row.push(m.r2_after === undefined ? "" : m.r2_after.toFixed(4));
        lines.push(row.map(csvCell).join(","));
    });
    const url = URL.createObjectURL(new Blob([lines.join("\r\n") + "\r\n"], { type: "text/csv" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "metabolite_batch_effects.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function MetaboliteBatchTable({ metabolites, afterMetabolites }) {
    const [showAll, setShowAll] = React.useState(false);
    const [search, setSearch] = React.useState("");
    const after = afterMetabolites ? Object.fromEntries(afterMetabolites.map((m) => [m.name, m])) : null;
    const rows = metabolites
        .map((m) => (after?.[m.name] ? { ...m, r2_after: after[m.name].r2_after, combat_skipped: after[m.name].combat_skipped } : m))
        .sort((a, b) => b.r2 - a.r2);
    const strong = rows.filter((m) => batchEffectLevel(m) === "strong").length;
    const moderate = rows.filter((m) => batchEffectLevel(m) === "moderate").length;
    const needle = search.trim().toLowerCase();
    const visible = rows.filter((m) => (showAll || batchEffectLevel(m)) && (!needle || m.name.toLowerCase().includes(needle)));

    return (
        <div className="mt-4">
            <h6 className="kit-reg-section-title">Metabolites with a batch effect</h6>
            <p style={{ fontSize: "0.82rem" }} className="mb-2">
                <strong>{strong}</strong> of {rows.length} metabolites have a strong batch effect and <strong>{moderate}</strong> a moderate one
                (FDR q &lt; {SIGNIFICANT_Q}, with batch explaining at least {percent(STRONG_R2, 0)} or {percent(MODERATE_R2, 0)} of the metabolite's variance).
                Values are from before any correction.
            </p>
            <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
                <input className="kit-reg-input" style={{ maxWidth: 220 }} placeholder="Search metabolites" value={search} onChange={(e) => setSearch(e.target.value)} />
                <div className="form-check mb-0">
                    <input id="qaqc-metab-all" className="form-check-input" type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
                    <label className="form-check-label" htmlFor="qaqc-metab-all" style={{ fontSize: "0.82rem" }}>Show all metabolites</label>
                </div>
                <button type="button" className="kit-reg-btn ms-auto" onClick={() => downloadMetabolitesCsv(rows)}>
                    <i className="bi bi-download me-1"></i>Download table (CSV)
                </button>
            </div>
            {visible.length === 0 ? (
                <div className="text-muted py-2" style={{ fontSize: "0.82rem" }}>
                    {needle ? "No metabolites match your search." : "No metabolite has a significant batch effect."}
                </div>
            ) : (
                <div className="qaqc-metab-scroll">
                    <table className="qaqc-stats-table">
                        <thead>
                            <tr>
                                <th>Metabolite</th>
                                <th title="Share of the metabolite's variance explained by batch (one-way ANOVA R²)">Explained by batch (R²)</th>
                                <th title="ANOVA p-value adjusted for testing many metabolites (Benjamini-Hochberg)">q (FDR)</th>
                                <th>Most different batch</th>
                                <th title="Values missing or below detection, imputed for the analysis">Missing</th>
                                {after && <th>R² after ComBat</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {visible.map((m) => {
                                const level = batchEffectLevel(m);
                                return (
                                    <tr key={m.name} className={level ? `qaqc-effect-${level}` : undefined}>
                                        <td>
                                            {m.name}
                                            {level && <span className={`qaqc-effect-badge qaqc-effect-badge-${level}`}>{level}</span>}
                                        </td>
                                        <td>
                                            {percent(m.r2)}
                                            <div className="qaqc-bar"><span style={{ width: percent(m.r2, 0) }}></span></div>
                                        </td>
                                        <td>{formatQ(m.q)}</td>
                                        <td>{m.most_different_batch ?? "–"} <small className="text-muted">{formatFold(m.fold_change)}</small></td>
                                        <td>
                                            {percent(m.missing_fraction, 0)}
                                            {m.missing_fraction > HIGH_MISSING && (
                                                <i className="bi bi-exclamation-triangle text-warning ms-1" title="Many values were imputed, which can create or exaggerate a batch effect"></i>
                                            )}
                                        </td>
                                        {after && <td>{m.r2_after === undefined ? "–" : m.combat_skipped ? "not corrected" : percent(m.r2_after)}</td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// Share of the variance in the top components that batch membership explains.
const batchVarianceOf = (result) =>
    result.explained_variance.reduce((sum, v, i) => sum + v * result.batch_r2[i], 0) /
    result.explained_variance.reduce((sum, v) => sum + v, 0);

const escapeHtml = (text) => String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function tooltipHtml(point) {
    let html = `<strong>${escapeHtml(point.sample)}</strong> &middot; ${escapeHtml(point.batch)}`;
    (point.details || []).forEach(([label, value]) => {
        html += `<div class="qaqc-tooltip-muted">${escapeHtml(label)}: ${escapeHtml(value)}</div>`;
    });
    return html;
}

function PcaPlot({ result, xPc, yPc, hidden, showEllipses }) {
    const svgRef = React.useRef(null);
    const tooltipRef = React.useRef(null);

    React.useEffect(() => {
        const svg = select(svgRef.current);
        svg.selectAll("*").remove();

        const batchIndex = Object.fromEntries(result.batches.map((b, i) => [b.label, i]));
        const visible = result.points.filter((p) => !hidden.has(p.batch));
        const all = result.points; // keep the axes stable when batches are toggled
        const xs = extent(all, (p) => p.scores[xPc]);
        const ys = extent(all, (p) => p.scores[yPc]);
        const pad = (lo, hi) => {
            const span = (hi - lo) || 1;
            return [lo - span * 0.08, hi + span * 0.08];
        };
        const x = scaleLinear().domain(pad(...xs)).range([MARGIN.left, WIDTH - MARGIN.right]);
        const y = scaleLinear().domain(pad(...ys)).range([HEIGHT - MARGIN.bottom, MARGIN.top]);

        // grid + zero lines
        svg.append("g").attr("transform", `translate(0,${HEIGHT - MARGIN.bottom})`)
            .call(axisBottom(x).ticks(8).tickSize(-(HEIGHT - MARGIN.top - MARGIN.bottom)).tickFormat(() => ""))
            .call((g) => { g.selectAll("line").attr("stroke", "#eee"); g.select(".domain").remove(); });
        svg.append("g").attr("transform", `translate(${MARGIN.left},0)`)
            .call(axisLeft(y).ticks(8).tickSize(-(WIDTH - MARGIN.left - MARGIN.right)).tickFormat(() => ""))
            .call((g) => { g.selectAll("line").attr("stroke", "#eee"); g.select(".domain").remove(); });
        const [x0, x1] = x.domain();
        const [y0, y1] = y.domain();
        if (x0 < 0 && x1 > 0) svg.append("line").attr("x1", x(0)).attr("x2", x(0)).attr("y1", MARGIN.top).attr("y2", HEIGHT - MARGIN.bottom).attr("stroke", "#bbb").attr("stroke-dasharray", "4 3");
        if (y0 < 0 && y1 > 0) svg.append("line").attr("y1", y(0)).attr("y2", y(0)).attr("x1", MARGIN.left).attr("x2", WIDTH - MARGIN.right).attr("stroke", "#bbb").attr("stroke-dasharray", "4 3");

        // axes
        svg.append("g").attr("transform", `translate(0,${HEIGHT - MARGIN.bottom})`).call(axisBottom(x).ticks(8)).attr("font-size", 11);
        svg.append("g").attr("transform", `translate(${MARGIN.left},0)`).call(axisLeft(y).ticks(8)).attr("font-size", 11);
        const axisLabel = (pc) => `PC${pc + 1} (${percent(result.explained_variance[pc])})`;
        svg.append("text").attr("x", (MARGIN.left + WIDTH - MARGIN.right) / 2).attr("y", HEIGHT - 10)
            .attr("text-anchor", "middle").attr("font-size", 13).attr("fill", "#333").text(axisLabel(xPc));
        svg.append("text").attr("transform", "rotate(-90)").attr("x", -(MARGIN.top + HEIGHT - MARGIN.bottom) / 2).attr("y", 16)
            .attr("text-anchor", "middle").attr("font-size", 13).attr("fill", "#333").text(axisLabel(yPc));

        // keep ellipses and points inside the axes
        svg.append("defs").append("clipPath").attr("id", "qaqc-plot-area").append("rect")
            .attr("x", MARGIN.left).attr("y", MARGIN.top)
            .attr("width", WIDTH - MARGIN.left - MARGIN.right).attr("height", HEIGHT - MARGIN.top - MARGIN.bottom);
        const plotArea = svg.append("g").attr("clip-path", "url(#qaqc-plot-area)");

        // confidence ellipses
        if (showEllipses) {
            result.batches.forEach((batch, i) => {
                if (hidden.has(batch.label)) return;
                const pts = visible.filter((p) => p.batch === batch.label).map((p) => [p.scores[xPc], p.scores[yPc]]);
                const ellipse = confidenceEllipse(pts);
                if (!ellipse) return;
                plotArea.append("path")
                    .attr("d", "M" + ellipse.map(([ex, ey]) => `${x(ex)},${y(ey)}`).join("L") + "Z")
                    .attr("fill", colorFor(i)).attr("fill-opacity", 0.1)
                    .attr("stroke", colorFor(i)).attr("stroke-opacity", 0.8).attr("stroke-width", 1.5);
            });
        }

        // points
        const tooltip = select(tooltipRef.current);
        plotArea.append("g").selectAll("circle").data(visible).join("circle")
            .attr("cx", (p) => x(p.scores[xPc]))
            .attr("cy", (p) => y(p.scores[yPc]))
            .attr("r", 5)
            .attr("fill", (p) => colorFor(batchIndex[p.batch]))
            .attr("fill-opacity", 0.8)
            .attr("stroke", "#fff").attr("stroke-width", 0.8)
            .on("mouseenter", function (event, p) {
                select(this).attr("r", 7).attr("stroke", "#000");
                tooltip.style("display", "block").html(tooltipHtml(p));
            })
            .on("mousemove", function (event) {
                const box = svgRef.current.parentNode.getBoundingClientRect();
                const x = event.clientX - box.left;
                // open towards the middle of the plot so the tooltip stays inside it
                tooltip.style("left", `${x + 12}px`).style("top", `${event.clientY - box.top + 12}px`)
                    .style("transform", x > box.width / 2 ? "translateX(calc(-100% - 24px))" : null);
            })
            .on("mouseleave", function () {
                select(this).attr("r", 5).attr("stroke", "#fff");
                tooltip.style("display", "none");
            });
    }, [result, xPc, yPc, hidden, showEllipses]);

    return (
        <div className="qaqc-plot-wrap">
            <svg ref={svgRef} viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="PCA score plot coloured by batch" />
            <div ref={tooltipRef} className="qaqc-tooltip" style={{ display: "none" }} />
        </div>
    );
}

export default function QAQC(props) {
    const pcaUrl = props.pcaUrl || "/qa_qc/pca";
    const submissionsUrl = props.submissionsUrl || "/qa_qc/submissions";
    const nextId = React.useRef(1);
    const fileInput = React.useRef(null);
    // Batches in plot order: [{ id, type: "file", file, label } | { id, type: "submission", submission, label }]
    const [items, setItems] = React.useState([]);
    const [submissions, setSubmissions] = React.useState(null); // null = loading
    const [submissionsError, setSubmissionsError] = React.useState(null);
    const [search, setSearch] = React.useState("");
    const [includeControls, setIncludeControls] = React.useState(false);
    const [layout, setLayout] = React.useState("auto");
    const [logTransform, setLogTransform] = React.useState(true);
    const [scaling, setScaling] = React.useState("auto");
    const [dragging, setDragging] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [result, setResult] = React.useState(null);
    const [xPc, setXPc] = React.useState(0);
    const [yPc, setYPc] = React.useState(1);
    const [hidden, setHidden] = React.useState(new Set());
    const [showEllipses, setShowEllipses] = React.useState(true);
    // ComBat: rerun of the displayed PCA (same inputs) with batch correction
    const lastRequest = React.useRef(null);
    const [corrected, setCorrected] = React.useState(null);
    const [showCorrected, setShowCorrected] = React.useState(false);
    const [correcting, setCorrecting] = React.useState(false);
    const [combatError, setCombatError] = React.useState(null);

    React.useEffect(() => {
        fetch(submissionsUrl, { headers: { Accept: "application/json" } })
            .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`status ${response.status}`))))
            .then(setSubmissions)
            .catch((e) => { setSubmissions([]); setSubmissionsError(`Could not load your submissions (${e.message}).`); });
    }, [submissionsUrl]);

    const selectedSubmissionIds = new Set(items.filter((it) => it.type === "submission").map((it) => it.submission.id));
    const toggleSubmission = (submission) => {
        setError(null);
        setItems((prev) => prev.some((it) => it.type === "submission" && it.submission.id === submission.id)
            ? prev.filter((it) => !(it.type === "submission" && it.submission.id === submission.id))
            : [...prev, { id: nextId.current++, type: "submission", submission, label: submission.name }]);
    };

    const visibleSubmissions = (submissions || []).filter((sub) => {
        const needle = search.trim().toLowerCase();
        return !needle || [sub.name, sub.project, sub.panel, sub.biofluid].some((v) => String(v || "").toLowerCase().includes(needle));
    });
    const submissionsByProject = visibleSubmissions.reduce((groups, sub) => {
        (groups[sub.project] = groups[sub.project] || []).push(sub);
        return groups;
    }, {});

    const addFiles = (fileList) => {
        const added = Array.from(fileList).filter((f) => /\.csv$/i.test(f.name));
        if (added.length < fileList.length) setError("Only .csv files can be added.");
        else setError(null);
        const newItems = added.map((file) => ({ id: nextId.current++, type: "file", file, label: baseName(file.name), prebatched: null }));
        setItems((prev) => [...prev, ...newItems]);
        newItems.forEach((item) => sniffPrebatched(item.file).then((prebatched) => {
            if (prebatched) setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, prebatched } : it)));
        }));
    };

    // A pre-batched CSV (only when samples are in rows) counts as one batch per value of its batch column.
    const prebatchedInfo = (item) => (item.type === "file" && layout !== "columns" ? item.prebatched : null);
    const batchCount = items.reduce((sum, item) => sum + (prebatchedInfo(item)?.batches || 1), 0);

    const requestPca = async (request, combat) => {
        const body = new FormData();
        let fileIndex = 0;
        const batches = request.items.map((item) => {
            if (item.type === "submission") return { type: "submission", id: item.submission.id, label: item.label };
            body.append("files[]", item.file);
            return { type: "file", index: fileIndex++, label: item.label };
        });
        body.append("batches", JSON.stringify(batches));
        body.append("include_controls", request.includeControls);
        body.append("layout", request.layout);
        body.append("log_transform", request.logTransform);
        body.append("scaling", request.scaling);
        body.append("combat", combat);
        const response = await fetch(pcaUrl, {
            method: "POST",
            headers: { "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")?.content, Accept: "application/json" },
            body,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `The request failed (${response.status}).`);
        return data;
    };

    const runPca = async () => {
        setLoading(true);
        setError(null);
        const request = { items, includeControls, layout, logTransform, scaling };
        try {
            const data = await requestPca(request, false);
            lastRequest.current = request;
            setResult(data);
            setCorrected(null);
            setShowCorrected(false);
            setCombatError(null);
            setHidden(new Set());
            setXPc(0);
            setYPc(Math.min(1, data.explained_variance.length - 1));
        } catch (e) {
            setResult(null);
            setCorrected(null);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const runCombat = async () => {
        setCorrecting(true);
        setCombatError(null);
        try {
            setCorrected(await requestPca(lastRequest.current, true));
            setShowCorrected(true);
        } catch (e) {
            setCombatError(e.message);
        } finally {
            setCorrecting(false);
        }
    };

    const toggleBatch = (label) => {
        setHidden((prev) => {
            const next = new Set(prev);
            next.has(label) ? next.delete(label) : next.add(label);
            return next;
        });
    };

    const shown = showCorrected && corrected ? corrected : result;
    const batchVariance = result ? batchVarianceOf(result) : 0;
    const shownBatchVariance = shown ? batchVarianceOf(shown) : 0;

    return (
        <div className="qaqc-layout">
            {/* LEFT: inputs */}
            <div className="kit-reg-card">
                <div className="kit-reg-section">
                    <h6 className="kit-reg-section-title"><i className="bi bi-1-circle me-2"></i>Batches</h6>
                    <label className="kit-reg-label">Pick from your profiled submissions</label>
                    <input className="kit-reg-input mb-1" placeholder="Search by name, project or panel" value={search} onChange={(e) => setSearch(e.target.value)} />
                    <div className="qaqc-picker">
                        {submissions === null && <div className="qaqc-picker-empty"><span className="spinner-border spinner-border-sm me-2"></span>Loading…</div>}
                        {submissions !== null && submissionsError && <div className="qaqc-picker-empty text-danger">{submissionsError}</div>}
                        {submissions !== null && !submissionsError && visibleSubmissions.length === 0 && (
                            <div className="qaqc-picker-empty">{submissions.length === 0 ? "You have no profiled submissions yet." : "No submissions match your search."}</div>
                        )}
                        {Object.entries(submissionsByProject).map(([project, subs]) => (
                            <div key={project}>
                                <div className="qaqc-picker-project">{project}</div>
                                {subs.map((sub) => (
                                    <label key={sub.id} className={`qaqc-picker-row${selectedSubmissionIds.has(sub.id) ? " is-selected" : ""}`}>
                                        <input type="checkbox" checked={selectedSubmissionIds.has(sub.id)} onChange={() => toggleSubmission(sub)} />
                                        <span className="qaqc-picker-name">{sub.name}</span>
                                        <small className="text-muted">{sub.panel} &middot; {sub.n_samples} samples &middot; {sub.created_at}</small>
                                    </label>
                                ))}
                            </div>
                        ))}
                    </div>

                    <label className="kit-reg-label mt-2">Or upload a CSV per batch</label>
                    <div
                        className={`qaqc-dropzone${dragging ? " is-dragging" : ""}`}
                        onClick={() => fileInput.current.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
                    >
                        <i className="bi bi-cloud-arrow-up fs-4 d-block"></i>
                        Drop CSV files here, or click to browse<br />
                        <small>One file per batch, or one file with a Batch column</small>
                        <input ref={fileInput} type="file" accept=".csv" multiple hidden
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
                    </div>
                    {items.length > 0 && (
                        <div className="mt-2">
                            {items.map((item, i) => (
                                <React.Fragment key={item.id}>
                                <div className="qaqc-batch-row">
                                    <span className="qaqc-swatch" style={{ background: colorFor(i) }}></span>
                                    <input className="kit-reg-input" value={item.label} maxLength={40} aria-label="Batch name"
                                        onChange={(e) => { const label = e.target.value; setItems((prev) => prev.map((it) => it.id === item.id ? { ...it, label } : it)); }} />
                                    <span className="qaqc-filename" title={item.type === "file" ? item.file.name : `Submission #${item.submission.id}`}>
                                        {item.type === "file" ? item.file.name : `Submission #${item.submission.id}`}
                                    </span>
                                    <button type="button" className="qaqc-remove" title="Remove"
                                        onClick={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}>&times;</button>
                                </div>
                                {prebatchedInfo(item) && (
                                    <div className="qaqc-prebatched-note">
                                        <i className="bi bi-collection me-1"></i>
                                        Contains {prebatchedInfo(item).batches} batches (from the "{prebatchedInfo(item).column}" column). It is split automatically.
                                    </div>
                                )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>

                <div className="kit-reg-section">
                    <h6 className="kit-reg-section-title"><i className="bi bi-2-circle me-2"></i>Options</h6>
                    <div className="kit-reg-field">
                        <label className="kit-reg-label" htmlFor="qaqc-layout">Layout of uploaded CSVs</label>
                        <select id="qaqc-layout" className="kit-reg-select" value={layout} onChange={(e) => setLayout(e.target.value)}>
                            <option value="auto">Auto-detect (LC-Autofit report or samples in rows)</option>
                            <option value="rows">Samples in rows, metabolites in columns</option>
                            <option value="columns">Samples in columns, metabolites in rows</option>
                        </select>
                    </div>
                    <div className="kit-reg-field">
                        <label className="kit-reg-label" htmlFor="qaqc-scaling">Scaling</label>
                        <select id="qaqc-scaling" className="kit-reg-select" value={scaling} onChange={(e) => setScaling(e.target.value)}>
                            <option value="auto">Autoscaling (unit variance)</option>
                            <option value="pareto">Pareto scaling</option>
                            <option value="none">None (mean-centre only)</option>
                        </select>
                    </div>
                    <div className="form-check mt-2">
                        <input id="qaqc-controls" className="form-check-input" type="checkbox" checked={includeControls} onChange={(e) => setIncludeControls(e.target.checked)} />
                        <label className="form-check-label" htmlFor="qaqc-controls" style={{ fontSize: "0.82rem" }}>Include QC and calibration samples (submissions only)</label>
                    </div>
                    <div className="form-check mt-2">
                        <input id="qaqc-log" className="form-check-input" type="checkbox" checked={logTransform} onChange={(e) => setLogTransform(e.target.checked)} />
                        <label className="form-check-label" htmlFor="qaqc-log" style={{ fontSize: "0.82rem" }}>Log2 transform concentrations</label>
                    </div>
                </div>

                <div className="kit-reg-section">
                    <button type="button" className="kit-reg-btn kit-reg-btn-primary w-100" disabled={batchCount < 2 || loading || items.some((it) => !it.label.trim())} onClick={runPca}>
                        {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Computing…</> : <><i className="bi bi-play-fill me-1"></i>Run PCA</>}
                    </button>
                    {batchCount < 2 && <small className="text-muted d-block mt-2">Select or upload at least two batches, or one CSV with a Batch column.</small>}
                    {batchCount >= 2 && items.some((it) => !it.label.trim()) && <small className="text-danger d-block mt-2">Give every batch a name.</small>}
                    {error && <div className="alert alert-danger py-2 mt-2 mb-0" style={{ fontSize: "0.8rem" }}>{error}</div>}
                </div>
            </div>

            {/* RIGHT: results */}
            <div className="kit-reg-card">
                {!result && (
                    <div className="text-center text-muted py-5">
                        <i className="bi bi-graph-up fs-1 d-block mb-2"></i>
                        Select submissions or upload CSVs (at least two batches) and run the PCA to see how they compare.
                    </div>
                )}
                {result && (
                    <>
                        <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                            {corrected && (
                                <div className="btn-group btn-group-sm mb-1" role="group" aria-label="Data shown">
                                    <button type="button" className={`btn ${showCorrected ? "btn-outline-secondary" : "btn-secondary"}`} onClick={() => setShowCorrected(false)}>Before</button>
                                    <button type="button" className={`btn ${showCorrected ? "btn-secondary" : "btn-outline-secondary"}`} onClick={() => setShowCorrected(true)}>After ComBat</button>
                                </div>
                            )}
                            <div>
                                <label className="kit-reg-label" htmlFor="qaqc-x">X axis</label>
                                <select id="qaqc-x" className="kit-reg-select" value={xPc} onChange={(e) => setXPc(Number(e.target.value))}>
                                    {shown.explained_variance.map((v, i) => <option key={i} value={i}>PC{i + 1} ({percent(v)})</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="kit-reg-label" htmlFor="qaqc-y">Y axis</label>
                                <select id="qaqc-y" className="kit-reg-select" value={yPc} onChange={(e) => setYPc(Number(e.target.value))}>
                                    {shown.explained_variance.map((v, i) => <option key={i} value={i}>PC{i + 1} ({percent(v)})</option>)}
                                </select>
                            </div>
                            <div className="form-check mb-2">
                                <input id="qaqc-ellipse" className="form-check-input" type="checkbox" checked={showEllipses} onChange={(e) => setShowEllipses(e.target.checked)} />
                                <label className="form-check-label" htmlFor="qaqc-ellipse" style={{ fontSize: "0.82rem" }}>95% ellipses</label>
                            </div>
                        </div>

                        <div className="mb-2">
                            {shown.batches.map((b, i) => (
                                <span key={b.label} className={`qaqc-legend-item${hidden.has(b.label) ? " is-hidden" : ""}`} onClick={() => toggleBatch(b.label)} title="Click to show/hide">
                                    <span className="qaqc-swatch" style={{ background: colorFor(i) }}></span>
                                    {b.label} <small className="text-muted">(n={b.n_samples})</small>
                                </span>
                            ))}
                        </div>

                        <PcaPlot result={shown} xPc={Math.min(xPc, shown.explained_variance.length - 1)} yPc={Math.min(yPc, shown.explained_variance.length - 1)} hidden={hidden} showEllipses={showEllipses} />

                        <div className="mt-3">
                            <h6 className="kit-reg-section-title">{shown.combat ? "After ComBat batch correction" : "Is there a batch effect?"}</h6>
                            {shown.combat ? (
                                <p style={{ fontSize: "0.82rem" }} className="mb-2">
                                    Batch membership now explains <strong>{percent(shownBatchVariance, 0)}</strong> of the variance captured by the top {shown.explained_variance.length} components
                                    (<strong>{percent(batchVariance, 0)}</strong> before correction).
                                    ComBat removes differences in mean and variance between batches, including any real biological differences that are confounded with batch,
                                    so only use it when the batches are expected to contain comparable samples.
                                </p>
                            ) : (
                                <p style={{ fontSize: "0.82rem" }} className="mb-2">
                                    Batch membership explains <strong>{percent(batchVariance, 0)}</strong> of the variance captured by the top {result.explained_variance.length} components.
                                    If batches form separate clusters along the leading PCs, batch correction may be worthwhile; if they overlap, it is probably not needed.
                                    Separation can also reflect real differences between the sample sets, so interpret it alongside your study design.
                                </p>
                            )}
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                {!corrected && (
                                    <button type="button" className="kit-reg-btn kit-reg-btn-primary" disabled={correcting} onClick={runCombat}
                                        title="Remove the batch effect with ComBat (empirical Bayes) and redo the PCA">
                                        {correcting ? <><span className="spinner-border spinner-border-sm me-2"></span>Correcting…</> : <><i className="bi bi-magic me-1"></i>Apply ComBat batch correction</>}
                                    </button>
                                )}
                                {corrected && (
                                    <button type="button" className="kit-reg-btn" onClick={() => downloadCorrectedCsv(corrected)}
                                        title="Corrected concentrations, samples in rows (values that were missing or below detection are left blank)">
                                        <i className="bi bi-download me-1"></i>Download corrected data (CSV)
                                    </button>
                                )}
                            </div>
                            {combatError && <div className="alert alert-danger py-2 mb-2" style={{ fontSize: "0.8rem" }}>{combatError}</div>}
                            <table className="qaqc-stats-table">
                                <thead>
                                    <tr><th>Component</th><th>Variance explained</th><th>Explained by batch (R²)</th></tr>
                                </thead>
                                <tbody>
                                    {shown.explained_variance.map((v, i) => (
                                        <tr key={i}>
                                            <td>PC{i + 1}</td>
                                            <td>{percent(v)}</td>
                                            <td>
                                                {percent(shown.batch_r2[i])}
                                                <div className="qaqc-bar"><span style={{ width: percent(shown.batch_r2[i], 0) }}></span></div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <small className="text-muted d-block mt-2">
                                {shown.points.length} samples &middot; {shown.n_features} metabolites used ({shown.n_features_shared} shared by all batches)
                            </small>
                            {shown.warnings.length > 0 && (
                                <ul className="mt-2 mb-0 text-muted" style={{ fontSize: "0.78rem" }}>
                                    {shown.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            )}
                        </div>

                        {result.metabolites && <MetaboliteBatchTable metabolites={result.metabolites} afterMetabolites={corrected?.metabolites} />}
                    </>
                )}
            </div>
        </div>
    );
}
